import { APIError, apiRequest } from "@/lib/api";

export const PLATFORM_INSTANCES_API_BASE = "/platform/v1/instances";
export const INSTANCE_BOOTSTRAP_API_PATH = "/api/v1/instance/bootstrap";

export const DEFAULT_INSTANCE_BRANDING: InstanceBranding = {
  primary_color: "#075c35",
  secondary_color: "#123f35",
  accent_color: "#14b87a",
  background_color: "#f7f2e8",
  surface_color: "#fffef8",
  text_color: "#071f17",
  muted_text_color: "#64726c",
  font_family: "Inter",
};

export type InstanceLifecycleState =
  | "draft"
  | "provisioning"
  | "ready"
  | "activating"
  | "active"
  | "suspended"
  | "failed"
  | "deleting"
  | "deleted"
  | string;

export type InstanceBranding = {
  logo_url?: string;
  favicon_url?: string;
  background_image_url?: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  background_color: string;
  surface_color: string;
  text_color: string;
  muted_text_color: string;
  font_family?: string;
};

export type PlatformInstance = {
  instance_id: string;
  slug: string;
  display_name: string;
  primary_url: string;
  region: string;
  release_channel: string;
  state: InstanceLifecycleState;
  provisioning_status: string;
  branding: InstanceBranding;
  branding_version?: number;
  configuration_version?: number;
  shard_generation?: number;
  target_shard_generation?: number;
  active_deployment_id?: string;
  maintenance_state: string;
  maintenance_operation_id?: string;
  row_version: number;
  boundaries: {
    data_shard_ready: boolean;
    storage_ready: boolean;
    auth_ready: boolean;
    gateway_ready: boolean;
    initial_admin_ready: boolean;
  };
  provisioning_operation_present: boolean;
  provisioning_operation_id?: string;
  initial_company_admin?: string;
  created_at?: string;
  updated_at?: string;
  activated_at?: string;
  last_error?: string;
};

export type PlatformInstanceProvisioningDispatch = {
  instance: PlatformInstance;
  operation_id: string;
  operation_state?: string;
  deployment_id?: string;
  target_shard_generation?: number;
  idempotent_replay?: boolean;
};

export type PlatformInstanceReplacementReason = "restore" | "region_move" | "boundary_replacement";

export type CreatePlatformInstanceReplacementInput = {
  reason: PlatformInstanceReplacementReason;
  target_region: string;
  release_channel: string;
  backup_ref?: string;
  expected_row_version: number;
  change_reason: string;
};

export type PlatformInstanceReplacementDispatch = {
  operation_id: string;
  deployment_id: string;
  source_deployment_id: string;
  source_generation: number;
  target_shard_generation: number;
  replacement_reason: PlatformInstanceReplacementReason;
  data_transfer_mode: "point_in_time_restore" | "quiesced_full_restore";
  seed_recovery_point_ref: string;
  operation_state: string;
  idempotent_replay?: boolean;
  action_redispatched?: boolean;
};

export type PlatformInstanceReplacementAction = {
  operation_id: string;
  operation_state: string;
  maintenance_state: string;
  source_deployment_id?: string;
  target_deployment_id?: string;
  activation_request_digest?: string;
  action_event_id?: string;
  action_redispatched?: boolean;
  idempotent_replay?: boolean;
};

export type PlatformInstanceProvisioningAction = {
  action_event_id: string;
  sequence: number;
  action: string;
  phase?: string;
  digest: string;
  status: string;
  result_status?: string;
  issued_at: string;
  expires_at: string;
  completed_at?: string;
  updated_at: string;
};

export type PlatformInstanceProvisioningResource = {
  resource_type: string;
  provider_resource_id: string;
  deterministic_name?: string;
  lifecycle_state: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
};

export type PlatformInstanceProvisioningOperationStatus = {
  instance: PlatformInstance;
  operation: {
    operation_id: string;
    operation_kind: string;
    replacement_reason?: PlatformInstanceReplacementReason;
    data_transfer_mode?: "point_in_time_restore" | "quiesced_full_restore";
    source_deployment_id?: string;
    target_shard_generation: number;
    controls_row_version?: number;
    controls_hash?: string;
    release_manifest_ref?: string;
    release_manifest_digest?: string;
    provider_ownership_name?: string;
    immutable_desired_state_digest?: string;
    state: string;
    stage: string;
    activation_request_digest?: string;
    cutover_checkpoint_ref?: string;
    cutover_checkpoint_digest?: string;
    quiesce_attestation_digest?: string;
    pointer_commit_digest?: string;
    pointer_committed_at?: string;
    maintenance_state?: string;
    attempt: number;
    resume_after?: string;
    created_at: string;
    updated_at: string;
    completed_at?: string;
  };
  deployment: {
    deployment_id: string;
    shard_generation: number;
    provider: string;
    provider_project_id?: string;
    provider_environment_id?: string;
    provider_service_ids: Record<string, string>;
    provider_volume_id?: string;
    provider_custom_domain_id?: string;
    region: string;
    lifecycle_state: string;
    replacement_mode?: PlatformInstanceReplacementReason;
    seed_recovery_point_ref?: string;
    seed_recovery_point_digest?: string;
    final_recovery_point_ref?: string;
    final_recovery_point_digest?: string;
    last_verified_at?: string;
  };
  recovery_point?: {
    ref: string;
    digest: string;
    cutoff_at: string;
    source_deployment_id: string;
    source_generation: number;
    recovery_epoch: string;
    storage_sequence: number;
    backup_ref: string;
    object_count: number;
    total_bytes: number;
  };
  journal: {
    ref: string;
    version: number;
    digest: string;
    resources: PlatformInstanceProvisioningResource[];
  };
  actions: PlatformInstanceProvisioningAction[];
  retry: {
    eligible: boolean;
    resume_after?: string;
  };
  dispatch?: {
    action_event_id: string;
    state: string;
    attempts: number;
    next_attempt_at?: string;
    error_code?: string;
    delivered_at?: string;
  };
  initial_admin_delivery?: {
    delivery_id: string;
    state: string;
    attempts: number;
    next_attempt_at?: string;
    error_code?: string;
    delivered_at?: string;
  };
  latest_result?: {
    result_sequence: number;
    status: string;
    result: Record<string, unknown>;
    received_at: string;
  };
  preflight?: {
    id: string;
    target_shard_generation: number;
    controls_row_version: number;
    controls_hash: string;
    release_manifest_ref: string;
    release_manifest_digest: string;
    phase_zero_approval_digest: string;
    provider_executable_manifest_digest: string;
    whole_cell_recovery_drill_evidence_digest: string;
    railway_workspace_id: string;
    railway_schema_fingerprint: string;
    railway_region_map_version: string;
    passed: boolean;
    gates: Array<{ name: string; passed: boolean; message: string }>;
    created_at: string;
    expires_at: string;
  };
};

export type PlatformInstanceList = {
  items: PlatformInstance[];
  total?: number;
  limit?: number;
  offset?: number;
  request_id?: string;
  generated_at?: string;
};

export type PlatformInstanceRecoveryPointSummary = {
  ref: string;
  digest: string;
  cutoff_at: string;
  source_deployment_id: string;
  source_generation: number;
  source_region: string;
  recovery_epoch: string;
  storage_sequence: number;
  backup_ref: string;
  object_count: number;
  total_bytes: number;
};

export type PlatformInstanceRecoveryPointList = {
  instance_id: string;
  items: PlatformInstanceRecoveryPointSummary[];
  total: number;
  limit: number;
};

export type CreatePlatformInstanceInput = {
  slug: string;
  display_name: string;
  primary_url: string;
  region?: string;
  release_channel?: string;
  initial_company_admin?: string;
  branding: InstanceBranding;
};

export type UpdatePlatformInstanceInput = Partial<
  Pick<
    CreatePlatformInstanceInput,
    "display_name" | "primary_url" | "region" | "release_channel" | "branding"
  >
>;

export type PublicInstanceBootstrap = {
  instance_id: string;
  slug: string;
  display_name: string;
  branding: InstanceBranding;
  branding_version?: number;
  configuration_version?: number;
  support_url?: string;
  legal_url?: string;
  locale?: string;
  timezone?: string;
  auth?: Record<string, unknown>;
  features?: Record<string, boolean>;
};

export type PlatformInstanceCommercialOverrides = {
  billing_mode: "" | "card" | "invoice" | "prepaid" | "manual_contract";
  funding_mode: "" | "prepaid" | "postpaid" | "hybrid";
  billing_interval: "" | "monthly" | "quarterly" | "annual" | "custom";
  currency: string;
  monthly_price_micros: number;
  compute_markup_basis_points: number;
  payment_terms_days: number;
  overage_allowed: boolean;
  support_tier: string;
};

export type PlatformInstanceHardLimits = {
  monthly_token_limit: number;
  monthly_credit_limit_micros: number;
  storage_byte_limit: number;
};

export type PlatformInstanceControls = {
  instance_id: string;
  provider_allowlist: string[];
  subscription_plan_code: string;
  commercial_overrides: PlatformInstanceCommercialOverrides;
  limits: PlatformInstanceHardLimits;
  configured: boolean;
  row_version: number;
  created_at?: string;
  updated_at?: string;
};

export type UpdatePlatformInstanceControlsInput = {
  provider_allowlist?: string[];
  subscription_plan_code?: string;
  commercial_overrides?: PlatformInstanceCommercialOverrides;
  limits?: PlatformInstanceHardLimits;
  change_reason: string;
};

export type LaunchPlatformInstanceInput = {
  instance: CreatePlatformInstanceInput;
  controls: UpdatePlatformInstanceControlsInput;
};

export type PlatformInstanceAggregateMetrics = {
  instance_id: string;
  shard_generation: number;
  source_version: number;
  source_updated_at: string;
  user_count: number;
  workspace_count: number;
  resource_count: number;
  job_count: number;
  storage_object_count: number;
  storage_bytes: number;
  monthly_token_committed: number;
  monthly_token_reserved: number;
  monthly_credit_committed_micros: number;
  projected_at?: string;
};

export type PlatformInstanceProjectionFreshness = {
  state: "fresh" | "delayed" | "stale" | "missing" | "invalid" | string;
  lag_seconds?: number;
  target_seconds: number;
  stale_after_seconds: number;
};

export type PlatformInstanceOverview = {
  instance: Pick<
    PlatformInstance,
    "instance_id" | "slug" | "display_name" | "state" | "primary_url" | "region"
  >;
  controls: PlatformInstanceControls;
  metrics: PlatformInstanceAggregateMetrics | null;
  source: string;
  as_of?: string;
  freshness: PlatformInstanceProjectionFreshness;
};

export type PlatformInstanceInventorySection = "summary" | "users" | "workspaces" | "resources";

export type PlatformInstanceInventoryFreshness = {
  state: "fresh" | "delayed" | "stale" | "missing" | "invalid";
  lag_seconds?: number;
  stale_after_seconds: number;
};

export type PlatformInstanceInventorySnapshot = {
  instance_id: string;
  shard_generation: number;
  snapshot_generation: number;
  source_updated_at: string;
  user_count: number;
  workspace_count: number;
  resource_count: number;
  projected_at: string;
};

export type PlatformInstanceInventoryUser = {
  local_id: string;
  email: string;
  display_name: string;
  status: string;
  role: string;
  is_company_admin: boolean;
  created_at: string;
  last_active_at?: string;
};

export type PlatformInstanceInventoryWorkspace = {
  local_id: string;
  display_name: string;
  type: string;
  status: string;
  owner_user_id: string;
  created_at: string;
  updated_at: string;
};

export type PlatformInstanceInventoryResource = {
  local_id: string;
  type: string;
  status: string;
  provider?: string;
  workspace_id?: string;
  user_id?: string;
  size_bytes: number;
  created_at: string;
  updated_at: string;
};

type PlatformInstanceInventoryPageBase<Section extends PlatformInstanceInventorySection, Item> = {
  instance_id: string;
  section: Section;
  as_of?: string;
  freshness: PlatformInstanceInventoryFreshness;
  snapshot: PlatformInstanceInventorySnapshot | null;
  items: Item[];
  total: number;
  limit: number;
  offset: number;
  next_offset?: number;
};

export type PlatformInstanceInventorySummary = PlatformInstanceInventoryPageBase<"summary", never>;
export type PlatformInstanceInventoryUsersPage = PlatformInstanceInventoryPageBase<
  "users",
  PlatformInstanceInventoryUser
>;
export type PlatformInstanceInventoryWorkspacesPage = PlatformInstanceInventoryPageBase<
  "workspaces",
  PlatformInstanceInventoryWorkspace
>;
export type PlatformInstanceInventoryResourcesPage = PlatformInstanceInventoryPageBase<
  "resources",
  PlatformInstanceInventoryResource
>;

export type PlatformInstanceInventoryPage =
  | PlatformInstanceInventorySummary
  | PlatformInstanceInventoryUsersPage
  | PlatformInstanceInventoryWorkspacesPage
  | PlatformInstanceInventoryResourcesPage;

export type ReadPlatformInstanceInventoryOptions<
  Section extends PlatformInstanceInventorySection = PlatformInstanceInventorySection,
> = {
  section?: Section;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
};

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};
}

function stringValue(value: unknown, fallback = "") {
  return typeof value === "string" ? value.trim() : fallback;
}

function requiredString(value: unknown, field: string) {
  const normalized = stringValue(value);
  if (!normalized) {
    throw new Error(`Instance response ${field} must be a non-empty string.`);
  }
  if (normalized.length > 2_048 || /[\u0000-\u001f\u007f]/.test(normalized)) {
    throw new Error(`Instance response ${field} contains unsupported characters.`);
  }
  return normalized;
}

function optionalString(value: unknown) {
  const normalized = stringValue(value);
  return normalized || undefined;
}

function positiveInteger(value: unknown) {
  const number = Number(value);
  return Number.isInteger(number) && number >= 0 ? number : undefined;
}

function nonNegativeInteger(value: unknown) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number >= 0 ? number : 0;
}

function requiredInteger(value: unknown, field: string, minimum = 0) {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < minimum) {
    throw new Error(`Instance response ${field} must be an integer of at least ${minimum}.`);
  }
  return value;
}

function optionalInteger(value: unknown, field: string, minimum = 0) {
  if (value === undefined || value === null) return undefined;
  return requiredInteger(value, field, minimum);
}

function requiredBoolean(value: unknown, field: string) {
  if (typeof value !== "boolean") {
    throw new Error(`Instance response ${field} must be a boolean.`);
  }
  return value;
}

function requiredRecord(value: unknown, field: string) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`Instance response ${field} must be an object.`);
  }
  return value as UnknownRecord;
}

function requiredInstanceID(value: unknown) {
  return requiredString(value, "instance_id");
}

function requiredInstanceSlug(value: unknown) {
  const slug = requiredString(value, "slug");
  if (!validateInstanceSlug(slug)) {
    throw new Error("Instance response slug is invalid.");
  }
  return slug;
}

function requiredPrimaryURL(value: unknown) {
  try {
    return normalizePrimaryURL(requiredString(value, "primary_url"));
  } catch {
    throw new Error("Instance response primary_url must be a safe wallet origin.");
  }
}

function inventoryString(record: UnknownRecord, key: string): string;
function inventoryString(record: UnknownRecord, key: string, optional: true): string | undefined;
function inventoryString(record: UnknownRecord, key: string, optional = false) {
  const value = record[key];
  if (optional && (value === undefined || value === null || value === "")) return undefined;
  if (typeof value !== "string" || !value.trim()) {
    throw new Error(`Instance inventory ${key} must be a non-empty string.`);
  }
  return value.trim();
}

function inventoryInteger(record: UnknownRecord, key: string, minimum = 0) {
  const value = record[key];
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < minimum) {
    throw new Error(`Instance inventory ${key} must be an integer of at least ${minimum}.`);
  }
  return value;
}

function inventoryBoolean(record: UnknownRecord, key: string) {
  const value = record[key];
  if (typeof value !== "boolean") {
    throw new Error(`Instance inventory ${key} must be a boolean.`);
  }
  return value;
}

function unwrapEnvelope(value: unknown): unknown {
  const record = asRecord(value);
  return record.data ?? record.result ?? value;
}

export function normalizeInstanceSlug(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 63);
}

export function validateInstanceSlug(value: string) {
  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(value) && !value.includes("--");
}

export function normalizePrimaryURL(value: string) {
  const raw = value.trim();
  if (!raw) throw new Error("Enter the wallet URL.");
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`;
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    throw new Error("Enter a valid wallet URL.");
  }
  const localhost = isDevelopmentHostname(parsed.hostname);
  if (parsed.protocol !== "https:" && !(localhost && parsed.protocol === "http:")) {
    throw new Error("Wallet URLs must use HTTPS (HTTP is allowed only for localhost).");
  }
  if (parsed.username || parsed.password || parsed.search || parsed.hash) {
    throw new Error("Wallet URLs cannot contain credentials, a query, or a fragment.");
  }
  if (parsed.pathname !== "/" && parsed.pathname !== "") {
    throw new Error("Use the wallet origin without a path.");
  }
  return parsed.origin;
}

export function normalizePublicAssetURL(value: string | undefined) {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  if (raw.startsWith("/")) {
    if (raw.startsWith("//")) throw new Error("Asset URLs cannot be protocol-relative.");
    return raw;
  }
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    throw new Error("Brand assets must use an HTTPS URL or a same-origin path.");
  }
  const localhost = isDevelopmentHostname(parsed.hostname);
  if (parsed.protocol !== "https:" && !(localhost && parsed.protocol === "http:")) {
    throw new Error("Brand assets must use HTTPS.");
  }
  if (parsed.username || parsed.password)
    throw new Error("Brand asset URLs cannot contain credentials.");
  return parsed.toString();
}

export function normalizeBrandColor(value: unknown, fallback: string) {
  const color = stringValue(value).toLowerCase();
  return /^#[0-9a-f]{6}$/.test(color) ? color : fallback;
}

export function normalizeFontFamily(value: unknown) {
  const font = stringValue(value, DEFAULT_INSTANCE_BRANDING.font_family || "Inter");
  return ["Inter", "DM Sans", "Manrope", "IBM Plex Sans", "Space Grotesk", "system-ui"].includes(
    font,
  )
    ? font
    : DEFAULT_INSTANCE_BRANDING.font_family;
}

function isDevelopmentHostname(value: string) {
  const hostname = value.toLowerCase().replace(/^\[|\]$/g, "");
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost") ||
    hostname.endsWith(".test")
  );
}

export function normalizeInstanceBranding(value: unknown): InstanceBranding {
  const branding = asRecord(value);
  return {
    logo_url: safeNormalizeAsset(branding.logo_url),
    favicon_url: safeNormalizeAsset(branding.favicon_url),
    background_image_url: safeNormalizeAsset(branding.background_image_url),
    primary_color: normalizeBrandColor(
      branding.primary_color,
      DEFAULT_INSTANCE_BRANDING.primary_color,
    ),
    secondary_color: normalizeBrandColor(
      branding.secondary_color,
      DEFAULT_INSTANCE_BRANDING.secondary_color,
    ),
    accent_color: normalizeBrandColor(
      branding.accent_color,
      DEFAULT_INSTANCE_BRANDING.accent_color,
    ),
    background_color: normalizeBrandColor(
      branding.background_color,
      DEFAULT_INSTANCE_BRANDING.background_color,
    ),
    surface_color: normalizeBrandColor(
      branding.surface_color,
      DEFAULT_INSTANCE_BRANDING.surface_color,
    ),
    text_color: normalizeBrandColor(branding.text_color, DEFAULT_INSTANCE_BRANDING.text_color),
    muted_text_color: normalizeBrandColor(
      branding.muted_text_color,
      DEFAULT_INSTANCE_BRANDING.muted_text_color,
    ),
    font_family: normalizeFontFamily(branding.font_family),
  };
}

function normalizePlatformInstanceBranding(value: unknown): InstanceBranding {
  const branding = requiredRecord(value, "branding");
  const fontFamily = requiredString(branding.font_family, "branding.font_family");
  if (normalizeFontFamily(fontFamily) !== fontFamily) {
    throw new Error("Instance response branding.font_family is unsupported.");
  }
  return validateInstanceBranding({
    logo_url: optionalString(branding.logo_url),
    favicon_url: optionalString(branding.favicon_url),
    background_image_url: optionalString(branding.background_image_url),
    primary_color: requiredString(branding.primary_color, "branding.primary_color"),
    secondary_color: requiredString(branding.secondary_color, "branding.secondary_color"),
    accent_color: requiredString(branding.accent_color, "branding.accent_color"),
    background_color: requiredString(branding.background_color, "branding.background_color"),
    surface_color: requiredString(branding.surface_color, "branding.surface_color"),
    text_color: requiredString(branding.text_color, "branding.text_color"),
    muted_text_color: requiredString(branding.muted_text_color, "branding.muted_text_color"),
    font_family: fontFamily,
  });
}

function safeNormalizeAsset(value: unknown) {
  try {
    return normalizePublicAssetURL(optionalString(value));
  } catch {
    return undefined;
  }
}

export function validateInstanceBranding(value: InstanceBranding): InstanceBranding {
  const colorFields = [
    "primary_color",
    "secondary_color",
    "accent_color",
    "background_color",
    "surface_color",
    "text_color",
    "muted_text_color",
  ] as const;
  for (const field of colorFields) {
    if (!/^#[0-9a-f]{6}$/i.test(value[field])) {
      throw new Error(`${field.replaceAll("_", " ")} must be a six-digit hex colour.`);
    }
  }
  const fontFamily = String(
    value.font_family || DEFAULT_INSTANCE_BRANDING.font_family || "Inter",
  ).trim();
  if (normalizeFontFamily(fontFamily) !== fontFamily) {
    throw new Error("font family is not supported.");
  }
  return {
    logo_url: normalizePublicAssetURL(value.logo_url),
    favicon_url: normalizePublicAssetURL(value.favicon_url),
    background_image_url: normalizePublicAssetURL(value.background_image_url),
    primary_color: value.primary_color.toLowerCase(),
    secondary_color: value.secondary_color.toLowerCase(),
    accent_color: value.accent_color.toLowerCase(),
    background_color: value.background_color.toLowerCase(),
    surface_color: value.surface_color.toLowerCase(),
    text_color: value.text_color.toLowerCase(),
    muted_text_color: value.muted_text_color.toLowerCase(),
    font_family: fontFamily,
  };
}

function normalizeInstanceRegion(value: string) {
  const region = value.trim().toLowerCase();
  if (!region || !validateInstanceSlug(region)) {
    throw new Error("Region must use letters, numbers, and single hyphens.");
  }
  return region;
}

function normalizeInstanceReleaseChannel(value: string) {
  const releaseChannel = value.trim().toLowerCase();
  if (!["stable", "pilot", "canary", "development"].includes(releaseChannel)) {
    throw new Error("Release channel must be stable, pilot, canary, or development.");
  }
  return releaseChannel;
}

export function prepareCreatePlatformInstanceInput(
  input: CreatePlatformInstanceInput,
): CreatePlatformInstanceInput {
  const slug = normalizeInstanceSlug(input.slug);
  if (!validateInstanceSlug(slug)) {
    throw new Error(
      "The slug must use letters, numbers, and single hyphens (maximum 63 characters).",
    );
  }
  const displayName = input.display_name.trim();
  if (displayName.length < 2 || displayName.length > 100) {
    throw new Error("Company name must be between 2 and 100 characters.");
  }
  const region = input.region === undefined ? undefined : normalizeInstanceRegion(input.region);
  const releaseChannel =
    input.release_channel === undefined
      ? undefined
      : normalizeInstanceReleaseChannel(input.release_channel);
  const initialCompanyAdmin = input.initial_company_admin?.trim().toLowerCase();
  if (initialCompanyAdmin && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(initialCompanyAdmin)) {
    throw new Error("Enter a valid initial company administrator email.");
  }
  return {
    slug,
    display_name: displayName,
    primary_url: normalizePrimaryURL(input.primary_url),
    ...(region ? { region } : {}),
    ...(releaseChannel ? { release_channel: releaseChannel } : {}),
    ...(initialCompanyAdmin ? { initial_company_admin: initialCompanyAdmin } : {}),
    branding: validateInstanceBranding(input.branding),
  };
}

export function prepareUpdatePlatformInstanceInput(
  input: UpdatePlatformInstanceInput,
): UpdatePlatformInstanceInput {
  const out: UpdatePlatformInstanceInput = {};
  if (input.display_name !== undefined) {
    const displayName = input.display_name.trim();
    if (displayName.length < 2 || displayName.length > 100) {
      throw new Error("Company name must be between 2 and 100 characters.");
    }
    out.display_name = displayName;
  }
  if (input.primary_url !== undefined) out.primary_url = normalizePrimaryURL(input.primary_url);
  if (input.region !== undefined) out.region = normalizeInstanceRegion(input.region);
  if (input.release_channel !== undefined) {
    out.release_channel = normalizeInstanceReleaseChannel(input.release_channel);
  }
  if (input.branding !== undefined) out.branding = validateInstanceBranding(input.branding);
  return out;
}

export function normalizePlatformInstance(value: unknown): PlatformInstance {
  const source = asRecord(unwrapEnvelope(value));
  const nested = asRecord(source.instance);
  const record = Object.keys(nested).length ? nested : source;
  const lifecycle = requiredString(record.state ?? record.status, "state");
  const boundaries = requiredRecord(record.boundaries, "boundaries");
  const instanceID = requiredInstanceID(record.instance_id ?? record.id);
  const slug = requiredInstanceSlug(record.slug);
  const primaryURL = requiredPrimaryURL(record.primary_url ?? record.primary_domain);
  return {
    instance_id: instanceID,
    slug,
    display_name: requiredString(record.display_name ?? record.name, "display_name"),
    primary_url: primaryURL,
    region: requiredString(record.region, "region"),
    release_channel: requiredString(record.release_channel, "release_channel"),
    state: lifecycle,
    provisioning_status: requiredString(record.provisioning_status, "provisioning_status"),
    branding: normalizePlatformInstanceBranding(record.branding),
    branding_version: optionalInteger(record.branding_version, "branding_version", 1),
    configuration_version: optionalInteger(
      record.configuration_version,
      "configuration_version",
      1,
    ),
    shard_generation: optionalInteger(record.shard_generation, "shard_generation", 1),
    target_shard_generation: optionalInteger(
      record.target_shard_generation,
      "target_shard_generation",
      1,
    ),
    active_deployment_id:
      optionalString(record.active_deployment_id) === undefined
        ? undefined
        : requiredDeploymentID(record.active_deployment_id, "active_deployment_id"),
    maintenance_state: requiredString(record.maintenance_state, "maintenance_state"),
    maintenance_operation_id: optionalProvisioningOperationID(record.maintenance_operation_id),
    row_version: requiredInteger(record.row_version, "row_version", 1),
    boundaries: {
      data_shard_ready: requiredBoolean(boundaries.data_shard_ready, "boundaries.data_shard_ready"),
      storage_ready: requiredBoolean(boundaries.storage_ready, "boundaries.storage_ready"),
      auth_ready: requiredBoolean(boundaries.auth_ready, "boundaries.auth_ready"),
      gateway_ready: requiredBoolean(boundaries.gateway_ready, "boundaries.gateway_ready"),
      initial_admin_ready: requiredBoolean(
        boundaries.initial_admin_ready,
        "boundaries.initial_admin_ready",
      ),
    },
    provisioning_operation_present: requiredBoolean(
      record.provisioning_operation_present,
      "provisioning_operation_present",
    ),
    provisioning_operation_id: optionalProvisioningOperationID(record.provisioning_operation_id),
    initial_company_admin: optionalString(record.initial_company_admin),
    created_at: optionalString(record.created_at),
    updated_at: optionalString(record.updated_at),
    activated_at: optionalString(record.activated_at),
    last_error: optionalString(record.provisioning_error) ?? optionalString(record.last_error),
  };
}

function optionalProvisioningOperationID(value: unknown) {
  const operationID = optionalString(value);
  if (operationID === undefined) return undefined;
  if (!/^prov_[0-9a-f]{32}(?:[0-9a-f]{32})?$/.test(operationID)) {
    throw new Error("Instance response provisioning_operation_id is invalid.");
  }
  return operationID;
}

export function normalizePlatformInstanceProvisioningDispatch(
  value: unknown,
): PlatformInstanceProvisioningDispatch {
  const record = asRecord(unwrapEnvelope(value));
  const instance = normalizePlatformInstance(record);
  const operationID = optionalProvisioningOperationID(record.operation_id);
  if (!operationID) {
    throw new Error("Instance provision response operation_id must be present.");
  }
  return {
    instance,
    operation_id: operationID,
    operation_state: optionalString(record.operation_state),
    deployment_id: optionalString(record.deployment_id),
    target_shard_generation: optionalInteger(
      record.target_shard_generation,
      "target_shard_generation",
      1,
    ),
    idempotent_replay:
      record.idempotent_replay === undefined
        ? undefined
        : requiredBoolean(record.idempotent_replay, "idempotent_replay"),
  };
}

function normalizePlatformInstanceReplacementReason(
  value: unknown,
): PlatformInstanceReplacementReason {
  const reason = requiredString(value, "replacement_reason");
  if (!["restore", "region_move", "boundary_replacement"].includes(reason)) {
    throw new Error("Instance replacement response reason is invalid.");
  }
  return reason as PlatformInstanceReplacementReason;
}

function optionalPlatformInstanceReplacementReason(
  value: unknown,
): PlatformInstanceReplacementReason | undefined {
  return optionalString(value) === undefined
    ? undefined
    : normalizePlatformInstanceReplacementReason(value);
}

function normalizePlatformInstanceTransferMode(
  value: unknown,
): "point_in_time_restore" | "quiesced_full_restore" {
  const mode = requiredString(value, "data_transfer_mode");
  if (!["point_in_time_restore", "quiesced_full_restore"].includes(mode)) {
    throw new Error("Instance replacement response transfer mode is invalid.");
  }
  return mode as "point_in_time_restore" | "quiesced_full_restore";
}

function optionalPlatformInstanceTransferMode(
  value: unknown,
): "point_in_time_restore" | "quiesced_full_restore" | undefined {
  return optionalString(value) === undefined
    ? undefined
    : normalizePlatformInstanceTransferMode(value);
}

function normalizePlatformInstanceReplacementDispatch(
  value: unknown,
): PlatformInstanceReplacementDispatch {
  const record = requiredRecord(unwrapEnvelope(value), "replacement dispatch");
  const operationID = optionalProvisioningOperationID(record.operation_id);
  if (!operationID) {
    throw new Error("Instance replacement response operation_id must be present.");
  }
  const normalized: PlatformInstanceReplacementDispatch = {
    operation_id: operationID,
    deployment_id: requiredDeploymentID(record.deployment_id, "deployment_id"),
    source_deployment_id: requiredDeploymentID(record.source_deployment_id, "source_deployment_id"),
    source_generation: requiredInteger(record.source_generation, "source_generation", 1),
    target_shard_generation: requiredInteger(
      record.target_shard_generation,
      "target_shard_generation",
      2,
    ),
    replacement_reason: normalizePlatformInstanceReplacementReason(record.replacement_reason),
    data_transfer_mode: normalizePlatformInstanceTransferMode(record.data_transfer_mode),
    seed_recovery_point_ref: requiredOpaqueReference(
      record.seed_recovery_point_ref,
      "seed_recovery_point_ref",
    ),
    operation_state: requiredString(record.operation_state, "operation_state"),
    idempotent_replay:
      record.idempotent_replay === undefined
        ? undefined
        : requiredBoolean(record.idempotent_replay, "idempotent_replay"),
    action_redispatched:
      record.action_redispatched === undefined
        ? undefined
        : requiredBoolean(record.action_redispatched, "action_redispatched"),
  };
  if (
    normalized.target_shard_generation <= normalized.source_generation ||
    (normalized.replacement_reason === "restore") !==
      (normalized.data_transfer_mode === "point_in_time_restore")
  ) {
    throw new Error(
      "Instance replacement response does not match its immutable generation and transfer mode.",
    );
  }
  return normalized;
}

function normalizePlatformInstanceReplacementAction(
  value: unknown,
): PlatformInstanceReplacementAction {
  const record = requiredRecord(unwrapEnvelope(value), "replacement action");
  const operationID = optionalProvisioningOperationID(record.operation_id);
  if (!operationID) {
    throw new Error("Instance replacement action operation_id must be present.");
  }
  return {
    operation_id: operationID,
    operation_state: requiredString(record.operation_state, "operation_state"),
    maintenance_state: requiredString(record.maintenance_state, "maintenance_state"),
    source_deployment_id:
      record.source_deployment_id === undefined
        ? undefined
        : requiredDeploymentID(record.source_deployment_id, "source_deployment_id"),
    target_deployment_id:
      record.target_deployment_id === undefined
        ? undefined
        : requiredDeploymentID(record.target_deployment_id, "target_deployment_id"),
    activation_request_digest: optionalString(record.activation_request_digest),
    action_event_id: optionalString(record.action_event_id),
    action_redispatched:
      record.action_redispatched === undefined
        ? undefined
        : requiredBoolean(record.action_redispatched, "action_redispatched"),
    idempotent_replay:
      record.idempotent_replay === undefined
        ? undefined
        : requiredBoolean(record.idempotent_replay, "idempotent_replay"),
  };
}

export function normalizePlatformInstanceProvisioningOperationStatus(
  value: unknown,
): PlatformInstanceProvisioningOperationStatus {
  const record = requiredRecord(unwrapEnvelope(value), "provisioning operation");
  const operation = requiredRecord(record.operation, "operation");
  const deployment = requiredRecord(record.deployment, "deployment");
  const journal = requiredRecord(record.journal, "journal");
  const retry = requiredRecord(record.retry, "retry");
  const operationID = optionalProvisioningOperationID(operation.operation_id);
  if (!operationID) {
    throw new Error("Instance response operation.operation_id must be present.");
  }
  const resourcesValue = journal.resources;
  if (!Array.isArray(resourcesValue)) {
    throw new Error("Instance response journal.resources must be an array.");
  }
  const actionsValue = record.actions;
  if (!Array.isArray(actionsValue)) {
    throw new Error("Instance response actions must be an array.");
  }
  const serviceIDs = requiredRecord(
    deployment.provider_service_ids,
    "deployment.provider_service_ids",
  );
  const normalizedServiceIDs: Record<string, string> = {};
  for (const [name, providerID] of Object.entries(serviceIDs)) {
    normalizedServiceIDs[requiredString(name, "provider service name")] = requiredString(
      providerID,
      `deployment.provider_service_ids.${name}`,
    );
  }
  const normalized: PlatformInstanceProvisioningOperationStatus = {
    instance: normalizePlatformInstance({ instance: record.instance }),
    operation: {
      operation_id: operationID,
      operation_kind: requiredString(operation.operation_kind, "operation.operation_kind"),
      replacement_reason: optionalPlatformInstanceReplacementReason(operation.replacement_reason),
      data_transfer_mode: optionalPlatformInstanceTransferMode(operation.data_transfer_mode),
      source_deployment_id:
        optionalString(operation.source_deployment_id) === undefined
          ? undefined
          : requiredDeploymentID(operation.source_deployment_id, "operation.source_deployment_id"),
      target_shard_generation: requiredInteger(
        operation.target_shard_generation,
        "operation.target_shard_generation",
        1,
      ),
      controls_row_version: optionalInteger(
        operation.controls_row_version,
        "operation.controls_row_version",
      ),
      controls_hash: optionalString(operation.controls_hash),
      release_manifest_ref: optionalString(operation.release_manifest_ref),
      release_manifest_digest: optionalString(operation.release_manifest_digest),
      provider_ownership_name: optionalString(operation.provider_ownership_name),
      immutable_desired_state_digest: optionalString(operation.immutable_desired_state_digest),
      state: requiredString(operation.state, "operation.state"),
      stage: requiredString(operation.stage, "operation.stage"),
      activation_request_digest: optionalString(operation.activation_request_digest),
      cutover_checkpoint_ref: optionalString(operation.cutover_checkpoint_ref),
      cutover_checkpoint_digest: optionalString(operation.cutover_checkpoint_digest),
      quiesce_attestation_digest: optionalString(operation.quiesce_attestation_digest),
      pointer_commit_digest: optionalString(operation.pointer_commit_digest),
      pointer_committed_at: optionalString(operation.pointer_committed_at),
      maintenance_state: optionalString(operation.maintenance_state),
      attempt: requiredInteger(operation.attempt, "operation.attempt", 1),
      resume_after: optionalString(operation.resume_after),
      created_at: requiredString(operation.created_at, "operation.created_at"),
      updated_at: requiredString(operation.updated_at, "operation.updated_at"),
      completed_at: optionalString(operation.completed_at),
    },
    deployment: {
      deployment_id: requiredDeploymentID(deployment.deployment_id, "deployment.deployment_id"),
      shard_generation: requiredInteger(
        deployment.shard_generation,
        "deployment.shard_generation",
        1,
      ),
      provider: requiredString(deployment.provider, "deployment.provider"),
      provider_project_id: optionalString(deployment.provider_project_id),
      provider_environment_id: optionalString(deployment.provider_environment_id),
      provider_service_ids: normalizedServiceIDs,
      provider_volume_id: optionalString(deployment.provider_volume_id),
      provider_custom_domain_id: optionalString(deployment.provider_custom_domain_id),
      region: requiredString(deployment.region, "deployment.region"),
      lifecycle_state: requiredString(deployment.lifecycle_state, "deployment.lifecycle_state"),
      replacement_mode: optionalPlatformInstanceReplacementReason(deployment.replacement_mode),
      seed_recovery_point_ref: optionalString(deployment.seed_recovery_point_ref),
      seed_recovery_point_digest: optionalString(deployment.seed_recovery_point_digest),
      final_recovery_point_ref: optionalString(deployment.final_recovery_point_ref),
      final_recovery_point_digest: optionalString(deployment.final_recovery_point_digest),
      last_verified_at: optionalString(deployment.last_verified_at),
    },
    journal: {
      ref: requiredString(journal.ref, "journal.ref"),
      version: requiredInteger(journal.version, "journal.version"),
      digest: requiredString(journal.digest, "journal.digest"),
      resources: resourcesValue.map((value, index) => {
        const resource = requiredRecord(value, `journal.resources.${index}`);
        return {
          resource_type: requiredString(
            resource.resource_type,
            `journal.resources.${index}.resource_type`,
          ),
          provider_resource_id: requiredString(
            resource.provider_resource_id,
            `journal.resources.${index}.provider_resource_id`,
          ),
          deterministic_name: optionalString(resource.deterministic_name),
          lifecycle_state: requiredString(
            resource.lifecycle_state,
            `journal.resources.${index}.lifecycle_state`,
          ),
          created_at: requiredString(resource.created_at, `journal.resources.${index}.created_at`),
          updated_at: requiredString(resource.updated_at, `journal.resources.${index}.updated_at`),
          deleted_at: optionalString(resource.deleted_at),
        };
      }),
    },
    actions: actionsValue.map((value, index) => {
      const action = requiredRecord(value, `actions.${index}`);
      return {
        action_event_id: requiredString(action.action_event_id, `actions.${index}.action_event_id`),
        sequence: requiredInteger(action.sequence, `actions.${index}.sequence`, 1),
        action: requiredString(action.action, `actions.${index}.action`),
        phase: optionalString(action.phase),
        digest: requiredString(action.digest, `actions.${index}.digest`),
        status: requiredString(action.status, `actions.${index}.status`),
        result_status: optionalString(action.result_status),
        issued_at: requiredString(action.issued_at, `actions.${index}.issued_at`),
        expires_at: requiredString(action.expires_at, `actions.${index}.expires_at`),
        completed_at: optionalString(action.completed_at),
        updated_at: requiredString(action.updated_at, `actions.${index}.updated_at`),
      };
    }),
    retry: {
      eligible: requiredBoolean(retry.eligible, "retry.eligible"),
      resume_after: optionalString(retry.resume_after),
    },
  };
  if (record.recovery_point !== undefined && record.recovery_point !== null) {
    const recoveryPoint = requiredRecord(record.recovery_point, "recovery_point");
    normalized.recovery_point = {
      ref: requiredOpaqueReference(recoveryPoint.ref, "recovery_point.ref"),
      digest: requiredDigest(recoveryPoint.digest, "recovery_point.digest"),
      cutoff_at: requiredString(recoveryPoint.cutoff_at, "recovery_point.cutoff_at"),
      source_deployment_id: requiredDeploymentID(
        recoveryPoint.source_deployment_id,
        "recovery_point.source_deployment_id",
      ),
      source_generation: requiredInteger(
        recoveryPoint.source_generation,
        "recovery_point.source_generation",
        1,
      ),
      recovery_epoch: requiredOpaqueReference(
        recoveryPoint.recovery_epoch,
        "recovery_point.recovery_epoch",
      ),
      storage_sequence: requiredInteger(
        recoveryPoint.storage_sequence,
        "recovery_point.storage_sequence",
      ),
      backup_ref: requiredOpaqueReference(recoveryPoint.backup_ref, "recovery_point.backup_ref"),
      object_count: requiredInteger(recoveryPoint.object_count, "recovery_point.object_count"),
      total_bytes: requiredInteger(recoveryPoint.total_bytes, "recovery_point.total_bytes"),
    };
  }
  if (normalized.operation.operation_kind === "replacement") {
    const reason = normalized.operation.replacement_reason;
    const transferMode = normalized.operation.data_transfer_mode;
    const point = normalized.recovery_point;
    if (
      !reason ||
      !transferMode ||
      !normalized.operation.source_deployment_id ||
      !point ||
      normalized.deployment.replacement_mode !== reason ||
      normalized.deployment.shard_generation !== normalized.operation.target_shard_generation ||
      normalized.deployment.seed_recovery_point_ref !== point.ref ||
      normalized.deployment.seed_recovery_point_digest !== point.digest ||
      point.source_deployment_id !== normalized.operation.source_deployment_id ||
      point.source_generation >= normalized.operation.target_shard_generation ||
      (reason === "restore") !== (transferMode === "point_in_time_restore")
    ) {
      throw new Error(
        "Instance replacement response does not match its immutable recovery and generation tuple.",
      );
    }
  } else if (
    normalized.operation.replacement_reason ||
    normalized.operation.data_transfer_mode ||
    normalized.operation.source_deployment_id ||
    normalized.deployment.replacement_mode ||
    normalized.recovery_point
  ) {
    throw new Error("Initial provisioning response contains replacement state.");
  }
  if (record.dispatch !== undefined && record.dispatch !== null) {
    const dispatch = requiredRecord(record.dispatch, "dispatch");
    normalized.dispatch = {
      action_event_id: requiredString(dispatch.action_event_id, "dispatch.action_event_id"),
      state: requiredString(dispatch.state, "dispatch.state"),
      attempts: requiredInteger(dispatch.attempts, "dispatch.attempts"),
      next_attempt_at: optionalString(dispatch.next_attempt_at),
      error_code: optionalString(dispatch.error_code),
      delivered_at: optionalString(dispatch.delivered_at),
    };
  }
  if (record.initial_admin_delivery !== undefined && record.initial_admin_delivery !== null) {
    const delivery = requiredRecord(record.initial_admin_delivery, "initial_admin_delivery");
    normalized.initial_admin_delivery = {
      delivery_id: requiredString(delivery.delivery_id, "initial_admin_delivery.delivery_id"),
      state: requiredString(delivery.state, "initial_admin_delivery.state"),
      attempts: requiredInteger(delivery.attempts, "initial_admin_delivery.attempts"),
      next_attempt_at: optionalString(delivery.next_attempt_at),
      error_code: optionalString(delivery.error_code),
      delivered_at: optionalString(delivery.delivered_at),
    };
  }
  if (record.latest_result !== undefined && record.latest_result !== null) {
    const latest = requiredRecord(record.latest_result, "latest_result");
    normalized.latest_result = {
      result_sequence: requiredInteger(latest.result_sequence, "latest_result.result_sequence", 1),
      status: requiredString(latest.status, "latest_result.status"),
      result: requiredRecord(latest.result, "latest_result.result"),
      received_at: requiredString(latest.received_at, "latest_result.received_at"),
    };
  }
  if (record.preflight !== undefined && record.preflight !== null) {
    const preflight = requiredRecord(record.preflight, "preflight");
    if (!Array.isArray(preflight.gates)) {
      throw new Error("Instance response preflight.gates must be an array.");
    }
    normalized.preflight = {
      id: requiredString(preflight.id, "preflight.id"),
      target_shard_generation: requiredInteger(
        preflight.target_shard_generation,
        "preflight.target_shard_generation",
        1,
      ),
      controls_row_version: requiredInteger(
        preflight.controls_row_version,
        "preflight.controls_row_version",
      ),
      controls_hash: requiredString(preflight.controls_hash, "preflight.controls_hash"),
      release_manifest_ref: requiredString(
        preflight.release_manifest_ref,
        "preflight.release_manifest_ref",
      ),
      release_manifest_digest: requiredString(
        preflight.release_manifest_digest,
        "preflight.release_manifest_digest",
      ),
      phase_zero_approval_digest: requiredString(
        preflight.phase_zero_approval_digest,
        "preflight.phase_zero_approval_digest",
      ),
      provider_executable_manifest_digest: requiredDigest(
        preflight.provider_executable_manifest_digest,
        "preflight.provider_executable_manifest_digest",
      ),
      whole_cell_recovery_drill_evidence_digest: requiredDigest(
        preflight.whole_cell_recovery_drill_evidence_digest,
        "preflight.whole_cell_recovery_drill_evidence_digest",
      ),
      railway_workspace_id: requiredString(
        preflight.railway_workspace_id,
        "preflight.railway_workspace_id",
      ),
      railway_schema_fingerprint: requiredString(
        preflight.railway_schema_fingerprint,
        "preflight.railway_schema_fingerprint",
      ),
      railway_region_map_version: requiredString(
        preflight.railway_region_map_version,
        "preflight.railway_region_map_version",
      ),
      passed: requiredBoolean(preflight.passed, "preflight.passed"),
      gates: preflight.gates.map((value, index) => {
        const gate = requiredRecord(value, `preflight.gates.${index}`);
        return {
          name: requiredString(gate.name, `preflight.gates.${index}.name`),
          passed: requiredBoolean(gate.passed, `preflight.gates.${index}.passed`),
          message: requiredString(gate.message, `preflight.gates.${index}.message`),
        };
      }),
      created_at: requiredString(preflight.created_at, "preflight.created_at"),
      expires_at: requiredString(preflight.expires_at, "preflight.expires_at"),
    };
    if (
      normalized.preflight.target_shard_generation !==
        normalized.operation.target_shard_generation ||
      (normalized.operation.controls_hash &&
        normalized.preflight.controls_hash !== normalized.operation.controls_hash) ||
      (normalized.operation.release_manifest_ref &&
        normalized.preflight.release_manifest_ref !== normalized.operation.release_manifest_ref) ||
      (normalized.operation.release_manifest_digest &&
        normalized.preflight.release_manifest_digest !==
          normalized.operation.release_manifest_digest)
    ) {
      throw new Error("Instance response preflight does not match the immutable operation target.");
    }
  }
  if (normalized.operation.operation_id !== operationID) {
    throw new Error("Instance provisioning operation scope does not match.");
  }
  return normalized;
}

export function normalizePlatformInstanceList(value: unknown): PlatformInstanceList {
  const unwrapped = unwrapEnvelope(value);
  const record = asRecord(unwrapped);
  let rawItems: unknown[];
  if (Array.isArray(unwrapped)) {
    rawItems = unwrapped;
  } else if (Array.isArray(record.items)) {
    rawItems = record.items;
  } else if (Array.isArray(record.instances)) {
    rawItems = record.instances;
  } else {
    throw new Error("Instance response instances must be an array.");
  }
  const items = rawItems.map(normalizePlatformInstance);
  if (new Set(items.map((item) => item.instance_id)).size !== items.length) {
    throw new Error("Instance response contains duplicate instance_id values.");
  }
  return {
    items,
    total: optionalInteger(record.total, "total"),
    limit: optionalInteger(record.limit, "limit", 1),
    offset: optionalInteger(record.offset, "offset"),
    request_id: optionalString(record.request_id),
    generated_at: optionalString(record.generated_at),
  };
}

export function normalizePlatformInstanceRecoveryPointList(
  value: unknown,
): PlatformInstanceRecoveryPointList {
  const record = requiredRecord(unwrapEnvelope(value), "instance recovery point list");
  if (!Array.isArray(record.items)) {
    throw new Error("Instance response recovery point items must be an array.");
  }
  const items = record.items.map((value, index) => {
    const point = requiredRecord(value, `recovery point ${index}`);
    const cutoffAt = requiredString(point.cutoff_at, `items.${index}.cutoff_at`);
    if (
      !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/.test(cutoffAt) ||
      !Number.isFinite(Date.parse(cutoffAt.replace(/\.(\d{3})\d+Z$/, ".$1Z")))
    ) {
      throw new Error(`Instance response items.${index}.cutoff_at is invalid.`);
    }
    return {
      ref: requiredOpaqueReference(point.ref, `items.${index}.ref`),
      digest: requiredDigest(point.digest, `items.${index}.digest`),
      cutoff_at: cutoffAt,
      source_deployment_id: requiredDeploymentID(
        point.source_deployment_id,
        `items.${index}.source_deployment_id`,
      ),
      source_generation: requiredInteger(
        point.source_generation,
        `items.${index}.source_generation`,
        1,
      ),
      source_region: requiredString(point.source_region, `items.${index}.source_region`),
      recovery_epoch: requiredOpaqueReference(
        point.recovery_epoch,
        `items.${index}.recovery_epoch`,
      ),
      storage_sequence: requiredInteger(point.storage_sequence, `items.${index}.storage_sequence`),
      backup_ref: requiredOpaqueReference(point.backup_ref, `items.${index}.backup_ref`),
      object_count: requiredInteger(point.object_count, `items.${index}.object_count`),
      total_bytes: requiredInteger(point.total_bytes, `items.${index}.total_bytes`),
    };
  });
  if (new Set(items.map((point) => point.ref)).size !== items.length) {
    throw new Error("Instance response contains duplicate recovery point refs.");
  }
  return {
    instance_id: requiredInstanceID(record.instance_id),
    items,
    total: requiredInteger(record.total, "total"),
    limit: requiredInteger(record.limit, "limit", 1),
  };
}

export function normalizePublicInstanceBootstrap(value: unknown): PublicInstanceBootstrap {
  const record = asRecord(unwrapEnvelope(value));
  const instance = asRecord(record.instance);
  const source = Object.keys(instance).length ? { ...record, ...instance } : record;
  const brandingRecord = asRecord(source.branding);
  const brandingSource = Object.keys(brandingRecord).length ? source.branding : source;
  const instanceID = requiredInstanceID(source.instance_id ?? source.id);
  const slug = requiredInstanceSlug(source.slug);
  return {
    instance_id: instanceID,
    slug,
    display_name: requiredString(source.display_name ?? source.name, "display_name"),
    branding: normalizeInstanceBranding(brandingSource),
    branding_version:
      positiveInteger(source.branding_version) ?? positiveInteger(brandingRecord.version),
    configuration_version: positiveInteger(source.configuration_version),
    support_url: safeNormalizeAsset(source.support_url),
    legal_url: safeNormalizeAsset(source.legal_url),
    locale: optionalString(source.locale),
    timezone: optionalString(source.timezone),
    auth: Object.keys(asRecord(source.auth)).length ? asRecord(source.auth) : undefined,
    features: normalizeBooleanMap(source.features),
  };
}

export function configuredPublicInstanceBootstrap(
  raw: unknown = import.meta.env.VITE_INSTANCE_BOOTSTRAP_JSON,
): PublicInstanceBootstrap | null {
  const value = String(raw || "").trim();
  if (!value) return null;
  if (value.length > 65_536) {
    throw new APIError(
      500,
      "INSTANCE_BOOTSTRAP_INVALID_CONFIGURATION",
      "The bundled instance bootstrap is too large.",
    );
  }
  try {
    return normalizePublicInstanceBootstrap(JSON.parse(value));
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(
      500,
      "INSTANCE_BOOTSTRAP_INVALID_CONFIGURATION",
      "The bundled instance bootstrap is invalid.",
    );
  }
}

function optionalEnum(value: unknown, field: string, allowed: string[]) {
  if (value === undefined || value === null || value === "") return "";
  const normalized = requiredString(value, field).toLowerCase();
  if (!allowed.includes(normalized)) {
    throw new Error(`Instance response ${field} is unsupported.`);
  }
  return normalized;
}

function optionalControlCode(value: unknown, field: string) {
  if (value === undefined || value === null || value === "") return "";
  const normalized = requiredString(value, field).toLowerCase();
  if (!/^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/.test(normalized)) {
    throw new Error(`Instance response ${field} is invalid.`);
  }
  return normalized;
}

export function normalizePlatformInstanceControls(value: unknown): PlatformInstanceControls {
  const source = asRecord(unwrapEnvelope(value));
  const nested = asRecord(source.controls);
  const record = Object.keys(nested).length ? nested : source;
  const commercial = requiredRecord(record.commercial_overrides, "commercial_overrides");
  const limits = requiredRecord(record.limits, "limits");
  if (!Array.isArray(record.provider_allowlist)) {
    throw new Error("Instance response provider_allowlist must be an array.");
  }
  const providers = normalizeProviderAllowlist(
    record.provider_allowlist.map((provider, index) =>
      requiredString(provider, `provider_allowlist[${index}]`),
    ),
  );
  const billingMode = optionalEnum(commercial.billing_mode, "commercial_overrides.billing_mode", [
    "card",
    "invoice",
    "prepaid",
    "manual_contract",
  ]);
  const fundingMode = optionalEnum(commercial.funding_mode, "commercial_overrides.funding_mode", [
    "prepaid",
    "postpaid",
    "hybrid",
  ]);
  const billingInterval = optionalEnum(
    commercial.billing_interval,
    "commercial_overrides.billing_interval",
    ["monthly", "quarterly", "annual", "custom"],
  );
  const currency = optionalString(commercial.currency)?.toUpperCase() ?? "";
  if (currency && !/^[A-Z]{3}$/.test(currency)) {
    throw new Error("Instance response commercial_overrides.currency is invalid.");
  }
  const supportTier = optionalControlCode(
    commercial.support_tier,
    "commercial_overrides.support_tier",
  );
  const subscriptionPlanCode = optionalControlCode(
    record.subscription_plan_code,
    "subscription_plan_code",
  );
  return {
    instance_id: requiredInstanceID(record.instance_id),
    provider_allowlist: providers,
    subscription_plan_code: subscriptionPlanCode,
    commercial_overrides: {
      billing_mode: billingMode as PlatformInstanceCommercialOverrides["billing_mode"],
      funding_mode: fundingMode as PlatformInstanceCommercialOverrides["funding_mode"],
      billing_interval: billingInterval as PlatformInstanceCommercialOverrides["billing_interval"],
      currency,
      monthly_price_micros:
        optionalInteger(
          commercial.monthly_price_micros,
          "commercial_overrides.monthly_price_micros",
        ) ?? 0,
      compute_markup_basis_points:
        optionalInteger(
          commercial.compute_markup_basis_points,
          "commercial_overrides.compute_markup_basis_points",
        ) ?? 0,
      payment_terms_days:
        optionalInteger(commercial.payment_terms_days, "commercial_overrides.payment_terms_days") ??
        0,
      overage_allowed: requiredBoolean(
        commercial.overage_allowed,
        "commercial_overrides.overage_allowed",
      ),
      support_tier: supportTier,
    },
    limits: {
      monthly_token_limit: requiredInteger(
        limits.monthly_token_limit,
        "limits.monthly_token_limit",
      ),
      monthly_credit_limit_micros: requiredInteger(
        limits.monthly_credit_limit_micros,
        "limits.monthly_credit_limit_micros",
      ),
      storage_byte_limit: requiredInteger(limits.storage_byte_limit, "limits.storage_byte_limit"),
    },
    configured: requiredBoolean(record.configured, "configured"),
    row_version: requiredInteger(record.row_version, "row_version"),
    created_at: optionalString(record.created_at),
    updated_at: optionalString(record.updated_at),
  };
}

export function normalizePlatformInstanceOverview(value: unknown): PlatformInstanceOverview {
  const record = requiredRecord(unwrapEnvelope(value), "overview");
  const identity = requiredRecord(record.instance, "instance");
  const hasMetrics = record.metrics !== null && record.metrics !== undefined;
  const metricsRecord = hasMetrics ? requiredRecord(record.metrics, "metrics") : {};
  const freshness = requiredRecord(record.freshness, "freshness");
  const instanceID = requiredInstanceID(identity.instance_id ?? identity.id);
  const controls = normalizePlatformInstanceControls(record.controls);
  if (controls.instance_id !== instanceID) {
    throw new Error("Instance overview controls scope does not match the instance.");
  }
  const freshnessState = requiredString(freshness.state, "freshness.state");
  if (!["fresh", "delayed", "stale", "missing", "invalid"].includes(freshnessState)) {
    throw new Error("Instance response freshness.state is unsupported.");
  }
  const metricsInstanceID = hasMetrics ? requiredInstanceID(metricsRecord.instance_id) : undefined;
  if (metricsInstanceID && metricsInstanceID !== instanceID) {
    throw new Error("Instance overview metrics scope does not match the instance.");
  }
  return {
    instance: {
      instance_id: instanceID,
      slug: requiredInstanceSlug(identity.slug),
      display_name: requiredString(identity.display_name, "display_name"),
      state: requiredString(identity.state ?? identity.status, "state"),
      primary_url: requiredPrimaryURL(identity.primary_url),
      region: requiredString(identity.region, "region"),
    },
    controls,
    metrics: hasMetrics
      ? {
          instance_id: metricsInstanceID as string,
          shard_generation: requiredInteger(
            metricsRecord.shard_generation,
            "metrics.shard_generation",
            1,
          ),
          source_version: requiredInteger(
            metricsRecord.source_version,
            "metrics.source_version",
            1,
          ),
          source_updated_at: requiredString(
            metricsRecord.source_updated_at,
            "metrics.source_updated_at",
          ),
          user_count: requiredInteger(metricsRecord.user_count, "metrics.user_count"),
          workspace_count: requiredInteger(
            metricsRecord.workspace_count,
            "metrics.workspace_count",
          ),
          resource_count: requiredInteger(metricsRecord.resource_count, "metrics.resource_count"),
          job_count: requiredInteger(metricsRecord.job_count, "metrics.job_count"),
          storage_object_count: requiredInteger(
            metricsRecord.storage_object_count,
            "metrics.storage_object_count",
          ),
          storage_bytes: requiredInteger(metricsRecord.storage_bytes, "metrics.storage_bytes"),
          monthly_token_committed: requiredInteger(
            metricsRecord.monthly_token_committed,
            "metrics.monthly_token_committed",
          ),
          monthly_token_reserved: requiredInteger(
            metricsRecord.monthly_token_reserved,
            "metrics.monthly_token_reserved",
          ),
          monthly_credit_committed_micros: requiredInteger(
            metricsRecord.monthly_credit_committed_micros,
            "metrics.monthly_credit_committed_micros",
          ),
          projected_at: optionalString(metricsRecord.projected_at),
        }
      : null,
    source: requiredString(record.source, "source"),
    as_of: optionalString(record.as_of),
    freshness: {
      state: freshnessState,
      lag_seconds:
        freshness.lag_seconds === undefined
          ? undefined
          : requiredInteger(freshness.lag_seconds, "freshness.lag_seconds"),
      target_seconds: requiredInteger(freshness.target_seconds, "freshness.target_seconds", 1),
      stale_after_seconds: requiredInteger(
        freshness.stale_after_seconds,
        "freshness.stale_after_seconds",
        1,
      ),
    },
  };
}

function normalizePlatformInstanceInventoryFreshness(
  value: unknown,
): PlatformInstanceInventoryFreshness {
  const record = asRecord(value);
  const state = inventoryString(record, "state") as PlatformInstanceInventoryFreshness["state"];
  if (!["fresh", "delayed", "stale", "missing", "invalid"].includes(state)) {
    throw new Error("Instance inventory freshness state is unsupported.");
  }
  return {
    state,
    lag_seconds:
      record.lag_seconds === undefined ? undefined : inventoryInteger(record, "lag_seconds"),
    stale_after_seconds: inventoryInteger(record, "stale_after_seconds"),
  };
}

function normalizePlatformInstanceInventorySnapshot(
  value: unknown,
): PlatformInstanceInventorySnapshot | null {
  if (value === null || value === undefined) return null;
  const record = asRecord(value);
  return {
    instance_id: inventoryString(record, "instance_id"),
    shard_generation: inventoryInteger(record, "shard_generation", 1),
    snapshot_generation: inventoryInteger(record, "snapshot_generation", 1),
    source_updated_at: inventoryString(record, "source_updated_at"),
    user_count: inventoryInteger(record, "user_count"),
    workspace_count: inventoryInteger(record, "workspace_count"),
    resource_count: inventoryInteger(record, "resource_count"),
    projected_at: inventoryString(record, "projected_at"),
  };
}

function normalizePlatformInstanceInventoryUser(value: unknown): PlatformInstanceInventoryUser {
  const record = asRecord(value);
  return {
    local_id: inventoryString(record, "local_id"),
    email: inventoryString(record, "email"),
    display_name: inventoryString(record, "display_name"),
    status: inventoryString(record, "status"),
    role: inventoryString(record, "role"),
    is_company_admin: inventoryBoolean(record, "is_company_admin"),
    created_at: inventoryString(record, "created_at"),
    last_active_at: inventoryString(record, "last_active_at", true),
  };
}

function normalizePlatformInstanceInventoryWorkspace(
  value: unknown,
): PlatformInstanceInventoryWorkspace {
  const record = asRecord(value);
  return {
    local_id: inventoryString(record, "local_id"),
    display_name: inventoryString(record, "display_name"),
    type: inventoryString(record, "type"),
    status: inventoryString(record, "status"),
    owner_user_id: inventoryString(record, "owner_user_id"),
    created_at: inventoryString(record, "created_at"),
    updated_at: inventoryString(record, "updated_at"),
  };
}

function normalizePlatformInstanceInventoryResource(
  value: unknown,
): PlatformInstanceInventoryResource {
  const record = asRecord(value);
  return {
    local_id: inventoryString(record, "local_id"),
    type: inventoryString(record, "type"),
    status: inventoryString(record, "status"),
    provider: inventoryString(record, "provider", true),
    workspace_id: inventoryString(record, "workspace_id", true),
    user_id: inventoryString(record, "user_id", true),
    size_bytes: record.size_bytes === undefined ? 0 : inventoryInteger(record, "size_bytes"),
    created_at: inventoryString(record, "created_at"),
    updated_at: inventoryString(record, "updated_at"),
  };
}

export function normalizePlatformInstanceInventory(
  value: unknown,
  expectedSection: PlatformInstanceInventorySection,
): PlatformInstanceInventoryPage {
  const record = asRecord(unwrapEnvelope(value));
  const section = inventoryString(record, "section") as PlatformInstanceInventorySection;
  if (!["summary", "users", "workspaces", "resources"].includes(section)) {
    throw new Error("Instance inventory section is unsupported.");
  }
  if (section !== expectedSection) {
    throw new Error(
      `Instance inventory returned section ${section} instead of ${expectedSection}.`,
    );
  }
  if (!Array.isArray(record.items)) {
    throw new Error("Instance inventory items must be an array.");
  }
  const instanceID = inventoryString(record, "instance_id");
  const snapshot = normalizePlatformInstanceInventorySnapshot(record.snapshot);
  if (snapshot && snapshot.instance_id !== instanceID) {
    throw new Error("Instance inventory snapshot scope does not match the response.");
  }
  const base = {
    instance_id: instanceID,
    as_of: inventoryString(record, "as_of", true),
    freshness: normalizePlatformInstanceInventoryFreshness(record.freshness),
    snapshot,
    total: inventoryInteger(record, "total"),
    limit: inventoryInteger(record, "limit", 1),
    offset: inventoryInteger(record, "offset"),
    next_offset:
      record.next_offset === undefined ? undefined : inventoryInteger(record, "next_offset", 1),
  };
  switch (section) {
    case "summary":
      if (record.items.length) {
        throw new Error("Instance inventory summary cannot contain item rows.");
      }
      return { ...base, section, items: [] };
    case "users":
      return {
        ...base,
        section,
        items: record.items.map(normalizePlatformInstanceInventoryUser),
      };
    case "workspaces":
      return {
        ...base,
        section,
        items: record.items.map(normalizePlatformInstanceInventoryWorkspace),
      };
    case "resources":
      return {
        ...base,
        section,
        items: record.items.map(normalizePlatformInstanceInventoryResource),
      };
  }
}

export function normalizeProviderAllowlist(value: string | string[]) {
  const values = Array.isArray(value) ? value : value.split(/[\n,]/g);
  const providers = values
    .map((provider) => provider.trim().toLowerCase())
    .filter(Boolean)
    .filter((provider, index, items) => items.indexOf(provider) === index)
    .sort();
  if (providers.length > 100) throw new Error("No more than 100 providers can be enabled.");
  if (providers.some((provider) => !/^[a-z0-9](?:[a-z0-9._-]{0,62}[a-z0-9])?$/.test(provider))) {
    throw new Error(
      "Provider names may contain lowercase letters, numbers, dots, underscores, and hyphens.",
    );
  }
  return providers;
}

export function decimalToMicros(value: string) {
  const normalized = value.trim().replace(/,/g, "");
  if (!/^\d+(?:\.\d{0,6})?$/.test(normalized)) {
    throw new Error("Enter a non-negative amount with no more than 6 decimal places.");
  }
  const [whole, fraction = ""] = normalized.split(".");
  const micros = BigInt(whole || "0") * 1_000_000n + BigInt(fraction.padEnd(6, "0") || "0");
  if (micros > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("The amount is too large.");
  return Number(micros);
}

export function microsToDecimal(value: number) {
  const safe = BigInt(nonNegativeInteger(value));
  const whole = safe / 1_000_000n;
  const fraction = (safe % 1_000_000n).toString().padStart(6, "0").replace(/0+$/, "");
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

export function basisPointsToPercent(value: number) {
  const normalized = nonNegativeInteger(value);
  return String(Math.round(normalized) / 100);
}

export function percentToBasisPoints(value: string) {
  const normalized = value.trim().replace(/,/g, "");
  if (!/^\d+(?:\.\d{0,2})?$/.test(normalized)) {
    throw new Error(
      "Compute markup must be a non-negative percentage with up to 2 decimal places.",
    );
  }
  const [whole, fraction = ""] = normalized.split(".");
  const basisPoints = BigInt(whole || "0") * 100n + BigInt(fraction.padEnd(2, "0") || "0");
  if (basisPoints > 100_000n) {
    throw new Error("Compute markup cannot exceed 1,000%.");
  }
  return Number(basisPoints);
}

export function gibToBytes(value: string) {
  const normalized = value.trim().replace(/,/g, "");
  if (!/^\d+(?:\.\d{0,3})?$/.test(normalized)) {
    throw new Error("Storage must be a non-negative GiB amount with up to 3 decimal places.");
  }
  const [whole, fraction = ""] = normalized.split(".");
  const thousandths = BigInt(whole || "0") * 1_000n + BigInt(fraction.padEnd(3, "0") || "0");
  const bytes = (thousandths * 1_073_741_824n) / 1_000n;
  if (bytes > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("The storage limit is too large.");
  return Number(bytes);
}

export function bytesToGiB(value: number) {
  return String(Math.round((nonNegativeInteger(value) / 1_073_741_824) * 1_000) / 1_000);
}

function normalizeBooleanMap(value: unknown) {
  const entries = Object.entries(asRecord(value)).filter((entry): entry is [string, boolean] => {
    return typeof entry[1] === "boolean";
  });
  return entries.length ? Object.fromEntries(entries) : undefined;
}

function requestInstanceID(value: string) {
  const instanceID = value.trim();
  if (!instanceID || instanceID.length > 255 || /[\u0000-\u001f\u007f]/.test(instanceID)) {
    throw new Error("A valid instance ID is required.");
  }
  return instanceID;
}

function requestProvisioningOperationID(value: string) {
  const operationID = optionalProvisioningOperationID(value);
  if (!operationID) {
    throw new Error("A valid provisioning operation ID is required.");
  }
  return operationID;
}

function requiredDeploymentID(value: unknown, field: string) {
  const deploymentID = requiredString(value, field);
  if (!/^dep_[0-9a-f]{16,128}$/.test(deploymentID)) {
    throw new Error(`Instance response ${field} is invalid.`);
  }
  return deploymentID;
}

function requiredOpaqueReference(value: unknown, field: string) {
  const reference = requiredString(value, field);
  if (!/^[A-Za-z0-9][A-Za-z0-9._:/+-]{0,511}$/.test(reference)) {
    throw new Error(`Instance response ${field} is invalid.`);
  }
  return reference;
}

function requiredDigest(value: unknown, field: string) {
  const digest = requiredString(value, field);
  if (!/^sha256:[0-9a-f]{64}$/.test(digest)) {
    throw new Error(`Instance response ${field} is invalid.`);
  }
  return digest;
}

function requestRowVersion(value: number) {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new Error("The instance row version must be a positive integer.");
  }
  return value;
}

function requestShardGeneration(value: number, field: string) {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new Error(`${field} must be a positive integer.`);
  }
  return value;
}

function requestChangeReason(value: string) {
  const reason = value.trim();
  if (
    reason.length < 8 ||
    reason.length > 500 ||
    /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(reason)
  ) {
    throw new Error("Enter an audit reason between 8 and 500 characters.");
  }
  return reason;
}

function requestControlsRowVersion(value: number) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error("The instance controls row version must be a non-negative integer.");
  }
  return value;
}

function assertInstanceScope<T extends { instance_id: string }>(
  expectedInstanceID: string,
  value: T,
  responseName: string,
) {
  if (value.instance_id !== expectedInstanceID) {
    throw new Error(`${responseName} scope does not match the requested instance.`);
  }
  return value;
}

function isAbortError(error: unknown) {
  return error instanceof Error && error.name === "AbortError";
}

export function listPlatformInstances(signal?: AbortSignal) {
  return apiRequest<unknown>(PLATFORM_INSTANCES_API_BASE, {
    signal,
    cache: "no-store",
  }).then(normalizePlatformInstanceList);
}

export function readPlatformInstance(instanceID: string, signal?: AbortSignal) {
  const requestedID = requestInstanceID(instanceID);
  return apiRequest<unknown>(`${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}`, {
    signal,
    cache: "no-store",
  })
    .then(normalizePlatformInstance)
    .then((instance) => assertInstanceScope(requestedID, instance, "Instance response"));
}

export function readPlatformInstanceRecoveryPoints(instanceID: string, signal?: AbortSignal) {
  const requestedID = requestInstanceID(instanceID);
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}/recovery-points?limit=100`,
    { signal, cache: "no-store" },
  )
    .then(normalizePlatformInstanceRecoveryPointList)
    .then((points) => assertInstanceScope(requestedID, points, "Instance recovery point response"));
}

export function readPlatformInstanceControls(instanceID: string, signal?: AbortSignal) {
  const requestedID = requestInstanceID(instanceID);
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}/controls`,
    { signal, cache: "no-store" },
  )
    .then(normalizePlatformInstanceControls)
    .then((controls) => assertInstanceScope(requestedID, controls, "Instance controls response"));
}

export function readPlatformInstanceOverview(instanceID: string, signal?: AbortSignal) {
  const requestedID = requestInstanceID(instanceID);
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}/overview`,
    { signal, cache: "no-store" },
  )
    .then(normalizePlatformInstanceOverview)
    .then((overview) => {
      assertInstanceScope(requestedID, overview.instance, "Instance overview response");
      return overview;
    });
}

export function readPlatformInstanceInventory<
  Section extends PlatformInstanceInventorySection = "summary",
>(
  instanceID: string,
  options: ReadPlatformInstanceInventoryOptions<Section> = {},
): Promise<Extract<PlatformInstanceInventoryPage, { section: Section }>> {
  let requestedID: string;
  try {
    requestedID = requestInstanceID(instanceID);
  } catch (error) {
    return Promise.reject(error);
  }
  const section = options.section ?? ("summary" as Section);
  if (!["summary", "users", "workspaces", "resources"].includes(section)) {
    return Promise.reject(new Error("Instance inventory section is unsupported."));
  }
  const parameters = new URLSearchParams({ section });
  if (section !== "summary") {
    const limit = options.limit ?? 50;
    const offset = options.offset ?? 0;
    if (!Number.isInteger(limit) || limit < 1 || limit > 200) {
      return Promise.reject(new Error("Instance inventory limit must be between 1 and 200."));
    }
    if (!Number.isSafeInteger(offset) || offset < 0) {
      return Promise.reject(new Error("Instance inventory offset must be a non-negative integer."));
    }
    parameters.set("limit", String(limit));
    parameters.set("offset", String(offset));
  }
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}/inventory?${parameters.toString()}`,
    { signal: options.signal, cache: "no-store" },
  ).then((value) => {
    const page = normalizePlatformInstanceInventory(value, section) as Extract<
      PlatformInstanceInventoryPage,
      { section: Section }
    >;
    return assertInstanceScope(requestedID, page, "Instance inventory response");
  });
}

function mutationKey(prefix: string, supplied?: string) {
  const value = String(supplied || "").trim();
  if (value.length >= 8) return value;
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}:${random}`;
}

export function createPlatformInstance(
  input: CreatePlatformInstanceInput,
  idempotencyKey?: string,
) {
  const prepared = prepareCreatePlatformInstanceInput(input);
  return apiRequest<unknown>(PLATFORM_INSTANCES_API_BASE, {
    method: "POST",
    body: prepared,
    headers: { "Idempotency-Key": mutationKey("instance:create", idempotencyKey) },
    cache: "no-store",
  }).then(normalizePlatformInstance);
}

export function launchPlatformInstance(
  input: LaunchPlatformInstanceInput,
  idempotencyKey?: string,
) {
  const instance = prepareCreatePlatformInstanceInput(input.instance);
  const providers = normalizeProviderAllowlist(input.controls.provider_allowlist || []);
  const planCode = String(input.controls.subscription_plan_code || "")
    .trim()
    .toLowerCase();
  if (!providers.length) throw new Error("Enable at least one provider before launching.");
  if (!input.controls.limits) throw new Error("Launch limits are required.");
  const changeReason = String(input.controls.change_reason || "").trim();
  if (changeReason.length < 8) throw new Error("Give an audit reason of at least 8 characters.");
  return apiRequest<unknown>(`${PLATFORM_INSTANCES_API_BASE}/launch`, {
    method: "POST",
    body: {
      instance,
      controls: {
        ...input.controls,
        provider_allowlist: providers,
        ...(planCode ? { subscription_plan_code: planCode } : {}),
        change_reason: changeReason,
      },
    },
    headers: { "Idempotency-Key": mutationKey("instance:launch", idempotencyKey) },
    cache: "no-store",
  }).then(normalizePlatformInstanceProvisioningDispatch);
}

export function updatePlatformInstance(
  instanceID: string,
  input: UpdatePlatformInstanceInput,
  expectedRowVersion: number,
  idempotencyKey?: string,
) {
  const requestedID = requestInstanceID(instanceID);
  const prepared = prepareUpdatePlatformInstanceInput(input);
  const rowVersion = requestRowVersion(expectedRowVersion);
  return apiRequest<unknown>(`${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}`, {
    method: "PATCH",
    body: { ...prepared, expected_row_version: rowVersion },
    headers: {
      "Idempotency-Key": mutationKey(`instance:update:${requestedID}`, idempotencyKey),
      "If-Match": String(rowVersion),
    },
    cache: "no-store",
  })
    .then(normalizePlatformInstance)
    .then((instance) => assertInstanceScope(requestedID, instance, "Instance update response"));
}

export function updatePlatformInstanceControls(
  instanceID: string,
  input: UpdatePlatformInstanceControlsInput,
  expectedRowVersion: number,
  idempotencyKey?: string,
) {
  const requestedID = requestInstanceID(instanceID);
  const rowVersion = requestControlsRowVersion(expectedRowVersion);
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}/controls`,
    {
      method: "PATCH",
      body: { ...input, expected_row_version: rowVersion },
      headers: {
        "Idempotency-Key": mutationKey(`instance:controls:${requestedID}`, idempotencyKey),
        "If-Match": String(rowVersion),
      },
      cache: "no-store",
    },
  )
    .then(normalizePlatformInstanceControls)
    .then((controls) => assertInstanceScope(requestedID, controls, "Instance controls response"));
}

export function provisionPlatformInstance(
  instanceID: string,
  expectedRowVersion: number,
  idempotencyKey?: string,
) {
  return provisionPlatformInstanceOperation(instanceID, expectedRowVersion, idempotencyKey).then(
    (result) => result.instance,
  );
}

export function provisionPlatformInstanceOperation(
  instanceID: string,
  expectedRowVersion: number,
  idempotencyKey?: string,
) {
  const requestedID = requestInstanceID(instanceID);
  const rowVersion = requestRowVersion(expectedRowVersion);
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}/provision`,
    {
      method: "POST",
      body: { expected_row_version: rowVersion },
      headers: {
        "Idempotency-Key": mutationKey(`instance:provision:${requestedID}`, idempotencyKey),
      },
      cache: "no-store",
    },
  )
    .then(normalizePlatformInstanceProvisioningDispatch)
    .then((result) => {
      assertInstanceScope(requestedID, result.instance, "Instance provision response");
      return result;
    });
}

export function createPlatformInstanceReplacement(
  instanceID: string,
  input: CreatePlatformInstanceReplacementInput,
  idempotencyKey?: string,
) {
  const requestedID = requestInstanceID(instanceID);
  const reason = String(input.reason || "")
    .trim()
    .toLowerCase();
  if (!["restore", "region_move", "boundary_replacement"].includes(reason)) {
    throw new Error("Replacement reason must be restore, region move, or boundary replacement.");
  }
  const targetRegion = normalizeInstanceRegion(input.target_region);
  if (!["eu-west", "us-east", "us-west"].includes(targetRegion)) {
    throw new Error("Replacement region must be eu-west, us-east, or us-west.");
  }
  const releaseChannel = normalizeInstanceReleaseChannel(input.release_channel);
  const backupRef = String(input.backup_ref || "").trim();
  if (
    (reason === "restore" && !/^[A-Za-z0-9][A-Za-z0-9._:/+-]{0,511}$/.test(backupRef)) ||
    (reason !== "restore" && backupRef !== "")
  ) {
    throw new Error(
      reason === "restore"
        ? "A valid immutable whole-cell recovery point reference is required."
        : "A recovery point reference is selected automatically for this replacement.",
    );
  }
  const rowVersion = requestRowVersion(input.expected_row_version);
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}/replacements`,
    {
      method: "POST",
      body: {
        reason,
        target_region: targetRegion,
        release_channel: releaseChannel,
        ...(backupRef ? { backup_ref: backupRef } : {}),
        expected_row_version: rowVersion,
        change_reason: requestChangeReason(input.change_reason),
      },
      headers: {
        "Idempotency-Key": mutationKey(
          `instance:replacement:create:${requestedID}`,
          idempotencyKey,
        ),
      },
      cache: "no-store",
    },
  )
    .then(normalizePlatformInstanceReplacementDispatch)
    .then((result) => {
      if (result.replacement_reason !== reason) {
        throw new Error("Instance replacement response reason does not match the request.");
      }
      return result;
    });
}

export function activatePlatformInstanceReplacement(
  instanceID: string,
  operationID: string,
  input: {
    expected_active_deployment_id: string;
    expected_active_generation: number;
    target_deployment_id: string;
    target_generation: number;
    confirm_point_in_time_restore: boolean;
    expected_row_version: number;
    change_reason: string;
  },
  idempotencyKey?: string,
) {
  const requestedID = requestInstanceID(instanceID);
  const requestedOperationID = requestProvisioningOperationID(operationID);
  const sourceDeploymentID = requiredDeploymentID(
    input.expected_active_deployment_id,
    "expected_active_deployment_id",
  );
  const targetDeploymentID = requiredDeploymentID(
    input.target_deployment_id,
    "target_deployment_id",
  );
  const sourceGeneration = requestShardGeneration(
    input.expected_active_generation,
    "Expected active generation",
  );
  const targetGeneration = requestShardGeneration(input.target_generation, "Target generation");
  if (targetGeneration <= sourceGeneration) {
    throw new Error("The replacement generation must be newer than the active generation.");
  }
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}` +
      `/replacements/${encodeURIComponent(requestedOperationID)}/activate`,
    {
      method: "POST",
      body: {
        expected_active_deployment_id: sourceDeploymentID,
        expected_active_generation: sourceGeneration,
        target_deployment_id: targetDeploymentID,
        target_generation: targetGeneration,
        confirm_point_in_time_restore: input.confirm_point_in_time_restore === true,
        expected_row_version: requestRowVersion(input.expected_row_version),
        change_reason: requestChangeReason(input.change_reason),
      },
      headers: {
        "Idempotency-Key": mutationKey(
          `instance:replacement:activate:${requestedID}:${requestedOperationID}`,
          idempotencyKey,
        ),
      },
      cache: "no-store",
    },
  )
    .then(normalizePlatformInstanceReplacementAction)
    .then((result) => {
      if (result.operation_id !== requestedOperationID) {
        throw new Error("Instance replacement activation response operation does not match.");
      }
      return result;
    });
}

export function revertPlatformInstanceReplacement(
  instanceID: string,
  operationID: string,
  reason: string,
  changeReason: string,
  idempotencyKey?: string,
) {
  const requestedID = requestInstanceID(instanceID);
  const requestedOperationID = requestProvisioningOperationID(operationID);
  const revertReason = reason.trim().toLowerCase();
  if (
    revertReason.length < 1 ||
    revertReason.length > 128 ||
    /[\u0000-\u001f\u007f]/.test(revertReason)
  ) {
    throw new Error("Enter a bounded cutover revert reason.");
  }
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}` +
      `/replacements/${encodeURIComponent(requestedOperationID)}/revert`,
    {
      method: "POST",
      body: {
        reason: revertReason,
        change_reason: requestChangeReason(changeReason),
      },
      headers: {
        "Idempotency-Key": mutationKey(
          `instance:replacement:revert:${requestedID}:${requestedOperationID}`,
          idempotencyKey,
        ),
      },
      cache: "no-store",
    },
  )
    .then(normalizePlatformInstanceReplacementAction)
    .then((result) => {
      if (result.operation_id !== requestedOperationID) {
        throw new Error("Instance replacement revert response operation does not match.");
      }
      return result;
    });
}

export function readPlatformInstanceProvisioningOperation(
  instanceID: string,
  operationID: string,
  signal?: AbortSignal,
) {
  const requestedID = requestInstanceID(instanceID);
  const requestedOperationID = requestProvisioningOperationID(operationID);
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}` +
      `/provisioning-operations/${encodeURIComponent(requestedOperationID)}`,
    { signal, cache: "no-store" },
  )
    .then(normalizePlatformInstanceProvisioningOperationStatus)
    .then((status) => {
      assertInstanceScope(requestedID, status.instance, "Instance provisioning status response");
      if (status.operation.operation_id !== requestedOperationID) {
        throw new Error(
          "Instance provisioning status operation does not match the requested operation.",
        );
      }
      return status;
    });
}

export function resumePlatformInstanceProvisioningOperation(
  instanceID: string,
  operationID: string,
  expectedRowVersion: number,
  reason: "provider_retry" | "dns_verified",
  idempotencyKey?: string,
) {
  const requestedID = requestInstanceID(instanceID);
  const requestedOperationID = requestProvisioningOperationID(operationID);
  const rowVersion = requestRowVersion(expectedRowVersion);
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}` +
      `/provisioning-operations/${encodeURIComponent(requestedOperationID)}/resume`,
    {
      method: "POST",
      body: { expected_row_version: rowVersion, reason },
      headers: {
        "Idempotency-Key": mutationKey(
          `instance:provision:resume:${requestedID}:${requestedOperationID}`,
          idempotencyKey,
        ),
      },
      cache: "no-store",
    },
  )
    .then(normalizePlatformInstanceProvisioningDispatch)
    .then((result) => {
      assertInstanceScope(requestedID, result.instance, "Instance provisioning resume response");
      if (result.operation_id !== requestedOperationID) {
        throw new Error(
          "Instance provisioning resume operation does not match the requested operation.",
        );
      }
      return result;
    });
}

export function activatePlatformInstance(
  instanceID: string,
  expectedRowVersion: number,
  idempotencyKey?: string,
) {
  const requestedID = requestInstanceID(instanceID);
  const rowVersion = requestRowVersion(expectedRowVersion);
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}/activate`,
    {
      method: "POST",
      body: { expected_row_version: rowVersion },
      headers: {
        "Idempotency-Key": mutationKey(`instance:activate:${requestedID}`, idempotencyKey),
      },
      cache: "no-store",
    },
  )
    .then(normalizePlatformInstance)
    .then((instance) => assertInstanceScope(requestedID, instance, "Instance activate response"));
}

export function activatePlatformInstanceDeployment(
  instanceID: string,
  expectedRowVersion: number,
  deploymentID: string,
  shardGeneration: number,
  idempotencyKey?: string,
) {
  const requestedID = requestInstanceID(instanceID);
  const rowVersion = requestRowVersion(expectedRowVersion);
  const requestedDeploymentID = deploymentID.trim();
  if (!/^dep_[0-9a-f]{16,128}$/.test(requestedDeploymentID)) {
    throw new Error("A valid target deployment ID is required.");
  }
  if (!Number.isSafeInteger(shardGeneration) || shardGeneration < 1) {
    throw new Error("The target shard generation must be a positive integer.");
  }
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}/activate`,
    {
      method: "POST",
      body: {
        expected_row_version: rowVersion,
        deployment_id: requestedDeploymentID,
        shard_generation: shardGeneration,
      },
      headers: {
        "Idempotency-Key": mutationKey(`instance:activate:${requestedID}`, idempotencyKey),
      },
      cache: "no-store",
    },
  )
    .then(normalizePlatformInstance)
    .then((instance) => assertInstanceScope(requestedID, instance, "Instance activate response"));
}

export function suspendPlatformInstance(
  instanceID: string,
  expectedRowVersion: number,
  idempotencyKey?: string,
) {
  const requestedID = requestInstanceID(instanceID);
  const rowVersion = requestRowVersion(expectedRowVersion);
  return apiRequest<unknown>(
    `${PLATFORM_INSTANCES_API_BASE}/${encodeURIComponent(requestedID)}/suspend`,
    {
      method: "POST",
      body: { expected_row_version: rowVersion },
      headers: {
        "Idempotency-Key": mutationKey(`instance:suspend:${requestedID}`, idempotencyKey),
      },
      cache: "no-store",
    },
  )
    .then(normalizePlatformInstance)
    .then((instance) => assertInstanceScope(requestedID, instance, "Instance suspend response"));
}

export async function readPublicInstanceBootstrap(signal?: AbortSignal) {
  // A company-specific Vercel build can carry a public, validated manifest.
  // This keeps branding host-local even when its isolated API has not yet
  // imported the control-plane directory row. VITE_* values are public by
  // design, so this manifest must never contain credentials or secrets.
  const configured = configuredPublicInstanceBootstrap();
  if (configured) return configured;

  // This endpoint is deliberately same-origin. The Host header is the trusted
  // instance selector, so the generic API client's hosted fallback list must
  // never be used here.
  let response: Response;
  try {
    response = await fetch(INSTANCE_BOOTSTRAP_API_PATH, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin",
      cache: "no-store",
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new APIError(
      503,
      "INSTANCE_BOOTSTRAP_UNAVAILABLE",
      error instanceof Error ? error.message : "The instance bootstrap is unavailable.",
    );
  }
  let payload: unknown;
  try {
    const contentType = response.headers.get("content-type") || "";
    payload = contentType.toLowerCase().includes("json")
      ? await response.json()
      : await response.text();
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new APIError(
      response.ok ? 502 : response.status,
      "INSTANCE_BOOTSTRAP_INVALID_RESPONSE",
      "The instance bootstrap returned an unreadable response.",
    );
  }
  if (!response.ok) {
    const record = asRecord(payload);
    const nested = asRecord(record.error);
    throw new APIError(
      response.status,
      stringValue(nested.code, stringValue(record.error_code, "INSTANCE_BOOTSTRAP_FAILED")),
      stringValue(
        nested.message,
        stringValue(record.error_detail, "The instance bootstrap could not be loaded."),
      ),
    );
  }
  try {
    return normalizePublicInstanceBootstrap(payload);
  } catch {
    throw new APIError(
      502,
      "INSTANCE_BOOTSTRAP_INVALID_RESPONSE",
      "The instance bootstrap returned an invalid configuration.",
    );
  }
}
